import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Phone, PhoneCall, Clock, Loader2, Search } from 'lucide-react';
import { inr, statusMeta } from './constants';
import SimulateCallModal from './SimulateCallModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mobile-responsive CRM Lead "Voice" dashboard: each lead card carries a
// "📞 Schedule Callback" action + a "🔊 Play Recording" HTML5 audio player.
export const LeadCallManager = ({ token, onOpenLead }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [scheduleFor, setScheduleFor] = useState(null); // lead id being scheduled
  const [whenVal, setWhenVal] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [simLead, setSimLead] = useState(null); // open WebRTC simulator for this lead

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, page_size: 60, sort: 'last_activity_at', order: 'desc' });
      if (q) params.set('q', q);
      const res = await fetch(`${API_URL}/api/crm/leads?${params}`, { headers });
      const d = await res.json();
      if (d.success) setLeads(d.leads || []);
    } catch (e) { console.error('call manager', e); }
    finally { setLoading(false); }
  }, [token, q]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const callNow = async (lead) => {
    if (!lead.phone) { toast.error('इस lead का phone नहीं है'); return; }
    setBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/trigger-ayush-call`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: lead.phone, session_id: lead.ai_session_id?.[0] || '' }) });
      const d = await res.json();
      if (d.success) toast.success('📞 Call शुरू — recording तैयार हो रही है');
      else toast.message('Call queue हुई', { description: 'Telephony provider connect होते ही dial होगा' });
      fetchLeads();
    } catch (e) { toast.error('Call trigger fail: ' + e.message); }
    finally { setBusyId(null); }
  };

  const scheduleCallback = async (lead) => {
    if (!whenVal) { toast.error('समय चुनें'); return; }
    setBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/schedule-ayush-callback`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: lead.ai_session_id?.[0] || lead.id, phone: lead.phone,
          scheduled_for: new Date(whenVal).toISOString() }) });
      const d = await res.json();
      if (d.success) { toast.success('⏰ Callback schedule हो गया'); setScheduleFor(null); setWhenVal(''); }
      else toast.error('Schedule नहीं हुआ');
    } catch (e) { toast.error('Schedule fail: ' + e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4 pb-28" data-testid="crm-call-manager">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="नाम / phone / school खोजें…"
          data-testid="call-manager-search"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> लोड हो रहा है…</div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 text-sm" data-testid="call-manager-empty">कोई lead नहीं मिला</div>
      ) : (
        // Mobile-first responsive grid: 1 col on phone → 2 (sm) → 3 (lg) → 4 (xl)
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="call-manager-grid">
          {leads.map((l) => {
            const meta = statusMeta(l.status);
            const hasRec = !!l.local_recording_path;
            return (
              <div key={l.id} data-testid={`call-card-${l.id}`}
                className="bg-white rounded-2xl border border-zinc-200 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => onOpenLead && onOpenLead(l.id)} className="text-left min-w-0" data-testid={`call-card-open-${l.id}`}>
                    <p className="font-semibold text-zinc-900 text-sm truncate">{l.name || 'अनाम Lead'}</p>
                    <p className="text-xs text-zinc-500 truncate">{l.school_name || '—'}</p>
                  </button>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>
                </div>

                {/* Meta row */}
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1 truncate"><Phone className="w-3 h-3" /> {l.phone || '—'}</span>
                  <span className="shrink-0 font-semibold text-zinc-700">Score {l.lead_score || 0}</span>
                </div>

                {/* Recording player */}
                {hasRec ? (
                  <div data-testid={`call-card-player-${l.id}`}>
                    <p className="text-[11px] text-zinc-400 mb-1">🔊 Recording</p>
                    <audio controls preload="none" className="w-full h-9"
                      src={`${API_URL}/api/live-chat/recording/${l.id}`} data-testid={`call-recording-audio-${l.id}`} />
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-300 italic" data-testid={`call-card-norec-${l.id}`}>अभी कोई recording नहीं</p>
                )}

                {/* Actions */}
                <div className="mt-auto flex flex-wrap gap-2">
                  <button onClick={() => callNow(l)} disabled={busyId === l.id}
                    data-testid={`call-now-${l.id}`}
                    className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                    {busyId === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />} अभी Call करें
                  </button>
                  <button onClick={() => { setScheduleFor(scheduleFor === l.id ? null : l.id); setWhenVal(''); }}
                    data-testid={`schedule-callback-${l.id}`}
                    className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800">
                    <Clock className="w-3.5 h-3.5" /> 📞 Schedule Callback
                  </button>
                  <button onClick={() => setSimLead(l)}
                    data-testid={`simulate-call-${l.id}`}
                    title="Browser mic से AI conversation test करें (sandbox, real call नहीं)"
                    className="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-indigo-300 text-indigo-700 bg-indigo-50 text-xs font-semibold hover:bg-indigo-100">
                    🎙️ Simulate WebRTC Call
                  </button>
                </div>

                {/* Inline scheduler */}
                {scheduleFor === l.id && (
                  <div className="flex flex-col gap-2 pt-1" data-testid={`schedule-panel-${l.id}`}>
                    <input type="datetime-local" value={whenVal} onChange={(e) => setWhenVal(e.target.value)}
                      data-testid={`schedule-datetime-${l.id}`}
                      className="h-9 px-2 rounded-lg border border-zinc-200 text-sm" />
                    <button onClick={() => scheduleCallback(l)} disabled={busyId === l.id || !whenVal}
                      data-testid={`schedule-confirm-${l.id}`}
                      className="h-9 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                      ⏰ Callback Set करें
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {simLead && <SimulateCallModal lead={simLead} token={token} onClose={() => setSimLead(null)} />}
    </div>
  );
};

export default LeadCallManager;
